<!-- eslint-disable -->
<script setup>
/* eslint-disable */
import { computed, inject, ref, watch } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emit = defineEmits(['navigate']);

const realData = inject('imudeiRealData');
const moduleKey = computed(() => 'brokers');
const screen = computed(() => 'create');
const runtimeModel = useImudeiRuntimeModel({
  realData,
  module: moduleKey,
  screen,
});

const step = ref(1);
const agencyName = ref('');
const agencyCreci = ref('');
const agencyCreciUf = ref('');
const profileNotice = ref('');

const steps = Object.freeze([
  'Identidade',
  'Contato',
  'CRECI',
  'Atuação',
  'Perfil',
  'Revisão',
]);

const unavailableFields = Object.freeze([
  ['Nome completo', 'Não disponibilizado pelo backend'],
  ['Nome social', 'Não disponibilizado pelo backend'],
  ['Sobre você', 'Não disponibilizado pelo backend'],
]);

const firstValue = (value, paths, fallback = '') => {
  const found = paths
    .map(path => path.split('.').reduce((item, key) => item?.[key], value))
    .find(item => item !== undefined && item !== null && item !== '');

  return found ?? fallback;
};

const initialsFor = value =>
  String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const profile = computed(() => realData.data.value?.profile || {});
const agencyEmail = computed(() =>
  firstValue(profile.value, ['email_contato', 'email'], '')
);
const agencyPhone = computed(() =>
  firstValue(profile.value, ['telefone', 'phone'], '')
);
const agencyLocation = computed(() => {
  const address = profile.value?.endereco || profile.value?.address || {};
  return firstValue(
    address,
    ['cidade', 'city', 'uf', 'state'],
    'Localização não disponibilizada'
  );
});
const agencyCreciLabel = computed(
  () =>
    [agencyCreci.value, agencyCreciUf.value].filter(Boolean).join(' / ') ||
    'CRECI não disponibilizado'
);
const canUpdateAgencyProfile = computed(
  () => typeof realData.actions?.brokers?.updateProfile === 'function'
);
const profileActionDisabled = computed(
  () => !canUpdateAgencyProfile.value || realData.saving.value
);
const creationUnavailableMessage = computed(
  () =>
    runtimeModel.unavailableMessage.value ||
    'O backend atual não oferece criação de corretor por esta rota.'
);
const teamMembers = computed(() =>
  runtimeModel.records.value
    .map(record => ({
      id: record.id,
      initials: initialsFor(record.title),
      name: record.title || 'Corretor sem nome',
      subtitle: record.subtitle || 'Sem função disponibilizada',
    }))
    .filter(member => member.id || member.name)
    .slice(0, 3)
);
const teamCount = computed(() => runtimeModel.records.value.length);
const profileStats = computed(() => {
  const metricByLabel = new Map(
    runtimeModel.metrics.value.map(metric => [metric.label, metric.value])
  );

  return [
    ['Corretores', metricByLabel.get('Corretores') || '—'],
    ['Anúncios', metricByLabel.get('Anúncios ativos') || '—'],
    ['Imóveis', metricByLabel.get('Imóveis') || '—'],
  ];
});

watch(
  profile,
  nextProfile => {
    agencyName.value = firstValue(nextProfile, ['nome_fantasia', 'name'], '');
    agencyCreci.value = firstValue(nextProfile, ['creci'], '');
    agencyCreciUf.value = firstValue(nextProfile, ['creci_uf'], '');
  },
  { immediate: true }
);

const saveAgencyProfile = async () => {
  if (profileActionDisabled.value) return;

  profileNotice.value = '';

  try {
    await realData.actions.brokers.updateProfile({
      nome_fantasia: agencyName.value,
      creci: agencyCreci.value,
      creci_uf: agencyCreciUf.value,
    });
    profileNotice.value = 'Dados da imobiliária atualizados.';
  } catch {
    profileNotice.value =
      realData.error.value ||
      'Não foi possível atualizar os dados da imobiliária.';
  }
};

const navigateDashboard = () => emit('navigate', 'dashboard');
</script>

<template>
  <main
    class="relative min-h-[947px] bg-[#fcfbff] pb-[112px] pl-3 pr-[30px] pt-3 text-[#101958]"
  >
    <div class="grid grid-cols-[minmax(0,1fr)_430px] items-start gap-[30px]">
      <section class="min-w-0">
        <header class="flex h-[77px] items-start gap-3 pt-1">
          <span
            class="grid size-9 place-items-center rounded-full bg-gradient-to-br from-[#67d7ff] via-[#9859ec] to-[#ff66ca] text-[20px] text-white"
          >
            ✦
          </span>
          <div>
            <h1 class="text-[24px] font-bold tracking-[-0.035em]">
              Criar corretor
            </h1>
            <p class="mt-1 text-[11px] text-[#5c6a90]">
              Conclua as etapas para adicionar um novo corretor e liberar bônus.
            </p>
          </div>
        </header>

        <section class="flex h-[42px] items-center px-6">
          <template v-for="(label, index) in steps" :key="label">
            <button
              class="flex min-w-0 items-center gap-2 text-left"
              type="button"
              @click="step = index + 1"
            >
              <span
                class="grid size-7 shrink-0 place-items-center rounded-full border text-[10px] font-bold"
                :class="
                  step === index + 1
                    ? 'border-[#5b25cc] bg-[#5b25cc] text-white'
                    : 'border-[#e3e0e9] bg-white text-[#5b6387]'
                "
              >
                {{ index + 1 }}
              </span>
              <span
                class="text-[10px]"
                :class="
                  step === index + 1
                    ? 'font-bold text-[#5b25cc]'
                    : 'text-[#5c6a90]'
                "
              >
                {{ label }}
              </span>
            </button>
            <i
              v-if="index < steps.length - 1"
              class="mx-3 h-px flex-1 bg-[#e9e6ee]"
            />
          </template>
        </section>

        <section
          class="mt-3 rounded-xl border border-[#ebe7f1] bg-white px-8 pb-4 pt-6 shadow-[0_3px_16px_rgba(50,29,96,0.045)]"
        >
          <header>
            <h2 class="text-[16px] font-bold">Quem é o corretor?</h2>
            <p class="mt-1 text-[11px] text-[#52618b]">
              Comece pela identidade para criarmos o perfil profissional.
            </p>
          </header>

          <p
            class="sr-only"
          >
            {{ creationUnavailableMessage }} A API atual fornece dados da
            imobiliária e da equipe, mas não expõe identidade, foto, perfil
            público ou convite de um novo corretor.
          </p>

          <div class="mt-3 grid grid-cols-[174px_minmax(0,1fr)] gap-6">
            <div class="text-center">
              <span
                class="relative mx-auto grid size-[146px] place-items-center rounded-full bg-gradient-to-br from-[#ff42c6] via-[#9961ef] to-[#17c9dd] p-1"
              >
                <span
                  class="grid size-full place-items-center rounded-full border-4 border-white bg-[#f3f0f8] text-[#7653d2]"
                >
                  <i class="i-lucide-user-round size-14" />
                </span>
                <i
                  class="absolute bottom-1 right-0 grid size-9 place-items-center rounded-full border-2 border-white bg-white text-[#8a94ae] shadow"
                >
                  <span class="i-lucide-image-off size-4" />
                </i>
              </span>
              <button
                class="mt-3 h-7 w-[145px] rounded-md border border-[#e3deea] text-[9px] font-semibold text-[#8992aa]"
                disabled
                type="button"
              >
                Foto indisponível
              </button>
              <small class="mt-2 block text-[8px] text-[#69759a]">
                O backend não recebe foto de corretor.
              </small>
            </div>

            <div class="grid grid-cols-2 gap-4">
              <label
                v-for="(field, index) in unavailableFields.slice(0, 2)"
                :key="field[0]"
                class="block text-[10px] font-semibold"
              >
                {{ field[0] }}
                <span v-if="index === 1" class="font-normal text-[#637096]">
                  (opcional)
                </span>
                <input
                  class="mt-2 h-9 w-full rounded-lg border border-[#e5e1eb] bg-[#faf9fc] px-3 text-[10px] font-medium text-[#8a93ab] outline-none"
                  :placeholder="field[1]"
                  disabled
                  type="text"
                />
              </label>

              <label class="col-span-2 block text-[10px] font-semibold">
                {{ unavailableFields[2][0] }}
                <span class="font-normal text-[#637096]">(opcional)</span>
                <textarea
                  class="mt-2 h-[86px] w-full resize-none rounded-lg border border-[#e5e1eb] bg-[#faf9fc] px-3 py-3 text-[10px] leading-4 text-[#8a93ab] outline-none"
                  :placeholder="unavailableFields[2][1]"
                  disabled
                />
                <small
                  class="-mt-4 mr-3 block text-right text-[8px] text-[#9ca3b5]"
                >
                  Sem limite retornado pela API
                </small>
              </label>
            </div>
          </div>

          <div
            class="mt-0 grid grid-cols-[174px_minmax(0,1fr)] items-center overflow-hidden rounded-lg border border-[#e4dfeb]"
          >
            <span class="px-5 py-4">
              <b class="block text-[10px]">
                Foto de capa
                <small class="font-normal text-[#66739a]">(opcional)</small>
              </b>
              <small class="mt-2 block text-[8px] text-[#677399]">
                Destaque o que te inspira.<br />
                Recurso não exposto pela API.
              </small>
            </span>
            <span
              class="relative block h-[100px] bg-gradient-to-r from-[#30215a] via-[#9259b2] to-[#f0a6b6]"
            >
              <span
                class="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.42),transparent_26%),radial-gradient(circle_at_74%_28%,rgba(255,255,255,0.3),transparent_20%)]"
              />
              <i
                class="absolute bottom-3 right-3 grid size-8 place-items-center rounded-full bg-white text-[#8a94ae] shadow"
              >
                <span class="i-lucide-image-off size-4" />
              </i>
            </span>
          </div>

          <div class="mt-3 flex justify-end">
            <button
              class="flex h-9 items-center gap-3 rounded-lg bg-[#ded8ea] px-8 text-[10px] font-bold text-[#858da3]"
              disabled
              type="button"
            >
              Continuar
              <i class="i-lucide-arrow-right size-4" />
            </button>
          </div>
        </section>

        <div class="mt-3 grid grid-cols-[1.08fr_0.92fr] gap-4">
          <article
            class="rounded-xl border border-[#ebe7f1] bg-white p-5 shadow-[0_3px_16px_rgba(50,29,96,0.045)]"
          >
            <header>
              <h2 class="text-[12px] font-bold">Perfil da imobiliária</h2>
              <p class="mt-1 text-[8px] text-[#657196]">
                Dados reais disponíveis para a operação atual.
              </p>
            </header>

            <div class="mt-4 grid grid-cols-[minmax(0,1fr)_72px] gap-2">
              <label class="block text-[8px] font-semibold">
                Nome da imobiliária
                <input
                  v-model="agencyName"
                  class="mt-1.5 h-8 w-full rounded-lg border border-[#ded9e8] px-3 text-[9px] font-medium outline-none focus:border-[#805ce1] disabled:bg-[#faf9fc]"
                  :disabled="profileActionDisabled"
                  placeholder="Não disponibilizado"
                  type="text"
                />
              </label>
              <label class="block text-[8px] font-semibold">
                UF
                <input
                  v-model="agencyCreciUf"
                  class="mt-1.5 h-8 w-full rounded-lg border border-[#ded9e8] px-3 text-[9px] font-medium uppercase outline-none focus:border-[#805ce1] disabled:bg-[#faf9fc]"
                  :disabled="profileActionDisabled"
                  maxlength="2"
                  placeholder="UF"
                  type="text"
                />
              </label>
            </div>

            <div class="mt-2 grid grid-cols-[minmax(0,1fr)_122px] gap-2">
              <label class="block text-[8px] font-semibold">
                CRECI da imobiliária
                <input
                  v-model="agencyCreci"
                  class="mt-1.5 h-8 w-full rounded-lg border border-[#ded9e8] px-3 text-[9px] font-medium outline-none focus:border-[#805ce1] disabled:bg-[#faf9fc]"
                  :disabled="profileActionDisabled"
                  placeholder="Não disponibilizado"
                  type="text"
                />
              </label>
              <button
                class="mt-[18px] h-8 rounded-lg bg-gradient-to-r from-[#4d1ab7] to-[#c82ecf] px-3 text-[8px] font-bold text-white disabled:cursor-not-allowed disabled:from-[#ded8ea] disabled:to-[#ded8ea] disabled:text-[#858da3]"
                :disabled="profileActionDisabled"
                type="button"
                @click="saveAgencyProfile"
              >
                {{ realData.saving.value ? 'Salvando…' : 'Salvar perfil' }}
              </button>
            </div>

            <p
              class="mt-3 min-h-4 text-[8px]"
              :class="
                profileNotice.includes('atualizados')
                  ? 'text-emerald-700'
                  : 'text-[#69759a]'
              "
            >
              {{
                profileNotice ||
                'Usa somente a ação existente de atualização do perfil da imobiliária.'
              }}
            </p>
          </article>

          <article
            class="rounded-xl border border-[#ebe7f1] bg-white p-5 shadow-[0_3px_16px_rgba(50,29,96,0.045)]"
          >
            <header>
              <h2 class="text-[12px] font-bold">
                Regras que valorizam o que importa
              </h2>
              <p class="mt-1 text-[8px] text-[#657196]">
                Reconhecimentos não são retornados pelo backend atual.
              </p>
            </header>
            <div class="mt-4 space-y-2.5 text-[8px]">
              <p class="flex items-center justify-between gap-3">
                <span class="flex items-center gap-2">
                  <i class="i-lucide-badge-check size-3 text-[#68749a]" />
                  Perfil completo e validado
                </span>
                <b class="font-semibold text-[#8b94ab]">Indisponível</b>
              </p>
              <p class="flex items-center justify-between gap-3">
                <span class="flex items-center gap-2">
                  <i class="i-lucide-calendar-check-2 size-3 text-[#68749a]" />
                  Disponibilidade atualizada
                </span>
                <b class="font-semibold text-[#8b94ab]">Indisponível</b>
              </p>
              <p class="flex items-center justify-between gap-3">
                <span class="flex items-center gap-2">
                  <i class="i-lucide-target size-3 text-[#68749a]" />
                  Marco real de meta
                </span>
                <b class="font-semibold text-[#8b94ab]">Indisponível</b>
              </p>
              <p
                class="flex items-center gap-2 border-t border-[#efebf4] pt-2.5 text-[#7b85a0]"
              >
                <i class="i-lucide-info size-3 shrink-0" />
                Ações repetidas e pontuação não têm contrato disponível nesta
                rota.
              </p>
            </div>
          </article>
        </div>
      </section>

      <aside class="space-y-3">
        <article
          class="overflow-hidden rounded-xl border border-[#ebe7f1] bg-white shadow-[0_3px_16px_rgba(50,29,96,0.045)]"
        >
          <header class="flex items-center justify-between px-5 pb-2 pt-3">
            <h2 class="text-[15px] font-bold">Dados da agência</h2>
            <button
              class="flex h-7 items-center gap-2 rounded-md border border-[#ded9e8] px-3 text-[8px] font-semibold text-[#3a267a]"
              type="button"
              @click="navigateDashboard"
            >
              <i class="i-lucide-users-round size-3" />
              Ver equipe
            </button>
          </header>

          <div
            class="relative h-[92px] bg-gradient-to-r from-[#432366] via-[#a060ad] to-[#f1b1c6]"
          >
            <span
              class="absolute inset-0 bg-[radial-gradient(circle_at_22%_16%,rgba(255,255,255,0.45),transparent_24%),radial-gradient(circle_at_76%_18%,rgba(255,255,255,0.27),transparent_22%)]"
            />
            <span
              class="absolute -bottom-11 left-5 grid size-[82px] place-items-center rounded-full border-4 border-white bg-gradient-to-br from-[#ff42c6] via-[#9961ef] to-[#17c9dd] text-white ring-2 ring-[#34d57a]"
            >
              <i class="i-lucide-building-2 size-9" />
            </span>
          </div>

          <div
            class="grid grid-cols-3 border-x border-b border-[#ece7f1] px-5 pb-2 pt-3 text-center"
          >
            <span v-for="stat in profileStats" :key="stat[0]">
              <b class="block text-[10px]">{{ stat[1] }}</b>
              <small class="text-[9px] text-[#667298]">{{ stat[0] }}</small>
            </span>
          </div>

          <div class="border-x border-b border-[#ece7f1] px-5 py-2">
            <h3 class="truncate text-[16px] font-bold">
              {{ agencyName || 'Imobiliária não disponibilizada' }}
            </h3>
            <p class="mt-1 truncate text-[10px] text-[#667298]">
              {{ agencyEmail || 'E-mail não disponibilizado' }}
              <b
                class="ml-2 rounded-full bg-[#f0eaff] px-2 py-1 text-[8px] text-[#5924cb]"
              >
                Imobiliária
              </b>
            </p>
            <p class="mt-2 text-[9px] text-[#657196]">
              {{ agencyCreciLabel }}
            </p>
            <div class="mt-2 flex flex-wrap gap-2">
              <span
                class="rounded-full bg-[#f2eff8] px-2 py-1 text-[8px] text-[#687398]"
              >
                {{ teamCount }} corretores retornados
              </span>
              <span
                class="rounded-full bg-[#f2eff8] px-2 py-1 text-[8px] text-[#687398]"
              >
                Perfil real
              </span>
            </div>
            <p class="mt-2 flex items-center gap-2 text-[9px] text-[#657196]">
              <span class="i-lucide-map-pin size-3" />
              {{ agencyLocation }}
            </p>
            <p
              v-if="agencyPhone"
              class="mt-2 flex items-center gap-2 text-[9px] text-[#657196]"
            >
              <span class="i-lucide-phone size-3" />
              {{ agencyPhone }}
            </p>
            <button
              class="mt-2 h-9 w-full rounded-lg border border-[#ded9e8] text-[9px] font-semibold text-[#3a267a]"
              type="button"
              @click="navigateDashboard"
            >
              Consultar equipe real
            </button>
          </div>
        </article>

        <article
          class="!mt-[18px] h-[199px] overflow-hidden rounded-xl border border-[#ebe7f1] bg-white p-4 shadow-[0_3px_16px_rgba(50,29,96,0.045)]"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[15px] font-bold">Bônus por perfil validado</h2>
            <span
              class="rounded bg-[#f6edff] px-2 py-1 text-[8px] text-[#a34fc5]"
            >
              SEM CONTRATO
            </span>
          </header>
          <p class="mt-1 text-[9px] text-[#647094]">
            A rota atual não retorna bônus, validação ou disponibilidade de
            corretores.
          </p>
          <strong class="mt-1 block text-[16px] text-[#7a859f]">
            Pontuação indisponível
          </strong>
          <div class="mt-3 space-y-2 text-[9px]">
            <p class="flex justify-between">
              <span>Perfil profissional completo</span>
              <span class="text-[#8a94aa]">—</span>
            </p>
            <p class="flex justify-between">
              <span>Dados validados</span>
              <span class="text-[#8a94aa]">—</span>
            </p>
            <p class="flex justify-between">
              <span>Disponibilidade atualizada</span>
              <span class="text-[#8a94aa]">—</span>
            </p>
          </div>
        </article>

        <article
          class="h-[200px] overflow-hidden rounded-xl border border-[#ebe7f1] bg-white p-4 shadow-[0_3px_16px_rgba(50,29,96,0.045)]"
        >
          <header class="flex items-center justify-between">
            <h2 class="text-[14px] font-bold">Dados disponíveis</h2>
            <button
              class="text-[9px] font-bold text-[#5520c8]"
              type="button"
              @click="navigateDashboard"
            >
              Ver equipe
            </button>
          </header>

          <div class="mt-3 space-y-3">
            <div class="flex gap-3">
              <span
                class="grid size-10 place-items-center rounded-full bg-[#eee9ff] text-[#5924cb]"
              >
                <i class="i-lucide-users-round size-4" />
              </span>
              <span class="flex-1">
                <b class="block text-[10px]">
                  Equipe carregada
                  <em class="float-right not-italic text-[#5924cb]">
                    {{ teamCount }} registros
                  </em>
                </b>
                <small class="mt-1 block text-[9px] text-[#657196]">
                  A lista retorna os corretores já existentes na operação.
                </small>
                <i
                  class="mt-3 block h-1.5 overflow-hidden rounded-full bg-[#e6e4eb]"
                >
                  <i
                    class="block h-full rounded-full bg-[#5821c9]"
                    :class="teamCount ? 'w-full' : 'w-0'"
                  />
                </i>
              </span>
            </div>

            <div class="flex gap-3">
              <span
                class="grid size-10 place-items-center rounded-full bg-[#eee9ff] text-[#5924cb]"
              >
                <i class="i-lucide-user-plus size-4" />
              </span>
              <span class="flex-1">
                <b class="block text-[10px]">
                  Criação de perfil
                  <em class="float-right not-italic text-[#8a94aa]">
                    Indisponível
                  </em>
                </b>
                <small class="mt-1 block text-[9px] text-[#657196]">
                  Nenhum endpoint de criação de corretor está disponível.
                </small>
                <i
                  class="mt-3 block h-1.5 overflow-hidden rounded-full bg-[#e6e4eb]"
                >
                  <i class="block h-full w-0 rounded-full bg-[#5821c9]" />
                </i>
              </span>
            </div>
          </div>
        </article>
      </aside>
    </div>

    <footer
      class="fixed bottom-5 left-[211px] right-[58px] z-30 flex h-[88px] items-center overflow-hidden rounded-2xl bg-gradient-to-r from-[#4c1dc2] via-[#d64bb9] to-[#3bcfe2] px-7 text-white shadow-[0_8px_22px_rgba(82,31,189,0.18)]"
    >
      <span
        class="grid size-12 place-items-center rounded-full border-4 border-white/60 text-[25px]"
      >
        <i class="i-lucide-shield-check size-7" />
      </span>
      <span class="ml-5">
        <b class="block text-[14px]">Dados reais constroem confiança.</b>
        <small class="mt-2 block text-[11px]">
          A criação só avança quando a API disponibiliza o contrato necessário.
        </small>
      </span>
      <span v-if="teamMembers.length" class="ml-auto flex -space-x-5">
        <span
          v-for="member in teamMembers"
          :key="member.id || member.name"
          class="grid size-14 place-items-center rounded-full border-4 border-white/70 bg-[#f5ecff] text-[11px] font-bold text-[#4f22bd]"
          :title="member.name"
        >
          {{ member.initials || '—' }}
        </span>
      </span>
      <span class="ml-8">
        <b class="block text-[15px]">A equipe está conectada.</b>
        <small class="mt-2 block text-[12px]">
          Consulte os corretores retornados pela sua operação.
        </small>
      </span>
    </footer>
  </main>
</template>
